'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { useLanguage } from '@/components/LanguageProvider'
import { translations } from '@/lib/translations'
import BugMascot from '@/components/BugMascot'
import { NEWSLETTER_FEEDBACK_PARAM, newsletterFeedbackPrefill, parseNewsletterFeedbackSlug } from '@/lib/newsletter/feedback-link'
import {
  type MascotMood,
  MOOD_DURATIONS,
  nextIdleDelay,
  pickIdleMood,
  hoverMood,
  successMood,
  errorMood,
  prefersReducedMotion,
} from '@/lib/bug-mascot-moods'
import {
  BUG_CATEGORIES,
  BUG_SEVERITIES,
  BUG_FREQUENCIES,
  BUG_ENVIRONMENTS,
  MAX_BUG_SCREENSHOTS,
  MAX_BUG_ATTACHMENTS,
  MAX_ATTACHMENT_BYTES,
  MAX_BUG_UPLOAD_BYTES,
  BUG_ATTACHMENT_EXTENSIONS,
  BUG_UPLOAD_ACCEPT,
  isAllowedAttachmentName,
  mbLabel,
  dataUrlBytes,
  bugUploadBytes,
  bugUploadTooLargeMessage,
  DEFAULT_BUG_PROJECT,
  areasForProject,
  examplesForProject,
  bugProjectLabel,
  hintForProject,
  type BugCategory,
  type BugSeverity,
  type BugFrequency,
  type BugEnvironment,
  type BugProject,
  type BugAttachment,
} from '@/lib/bug-reports'
// The picker offers ONLY the apps the pipeline actually works — the same list the stage machine
// reads, so the form can never offer an app whose reports nothing would pick up. Safe in a client
// bundle: lib/bug-pipeline is a pure core whose only imports are types.
import { PIPELINE_PROJECTS } from '@/lib/bug-pipeline'
// The pasted-credential detector (BUG-muzhg0nt-n81q slice 2) — a LEAF with no imports, so this
// client component holds the SAME rule the API refuses by rather than a second guess at it.
import { screenPastedCredentialWrite, PASTED_CREDENTIAL_OVERRIDE_KEY } from '@/lib/pasted-credentials'

// ── Types ────────────────────────────────────────────────────────────────────

interface FormState {
  title: string
  description: string
  expectedBehavior: string
  stepsToReproduce: string
  category: BugCategory | ''
  severity: BugSeverity | ''
  affectedArea: string
  customArea: string
  environment: BugEnvironment
  frequency: BugFrequency | ''
  blockedWork: boolean
  workaroundFound: boolean
  workaroundDetails: string
  /** Reporter identity, collected in the form only when there is no session. */
  reporterName: string
  reporterEmail: string
}

const EMPTY_FORM: FormState = {
  title: '',
  description: '',
  expectedBehavior: '',
  stepsToReproduce: '',
  category: '',
  severity: '',
  affectedArea: '',
  customArea: '',
  environment: 'production',
  frequency: '',
  blockedWork: false,
  workaroundFound: false,
  workaroundDetails: '',
  reporterName: '',
  reporterEmail: '',
}

/**
 * Remembers who filed from a device that has no session, so the reporter
 * types their name once instead of on every bug. Local to the browser — the
 * server never trusts it for anything but the notification address.
 */
const REPORTER_STORAGE_KEY = 'bug-reporter-identity'

interface Props {
  /** Current user email from session. */
  userEmail?: string | null
  /** Current user name from session. */
  userName?: string | null
  /**
   * Which app the filed bug belongs to. One bug DB, several products — the
   * mount point decides the tag, never the reporter, so a bug filed from the
   * driver app can never land in the Portal queue by accident.
   */
  project?: BugProject
  /**
   * Distance from the bottom of the viewport. The default clears nothing in
   * particular; surfaces with a fixed bottom bar (the driver app's tab bar)
   * pass a larger value so the button doesn't sit on top of it.
   */
  offsetBottom?: string
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function getBrowserInfo(): string {
  if (typeof navigator === 'undefined') return 'unknown'
  const ua = navigator.userAgent
  if (ua.includes('Chrome') && !ua.includes('Edg')) return `Chrome ${ua.match(/Chrome\/([\d.]+)/)?.[1] ?? ''}`
  if (ua.includes('Edg')) return `Edge ${ua.match(/Edg\/([\d.]+)/)?.[1] ?? ''}`
  if (ua.includes('Firefox')) return `Firefox ${ua.match(/Firefox\/([\d.]+)/)?.[1] ?? ''}`
  if (ua.includes('Safari') && !ua.includes('Chrome')) return `Safari ${ua.match(/Version\/([\d.]+)/)?.[1] ?? ''}`
  return ua.slice(0, 80)
}

function getOsInfo(): string {
  if (typeof navigator === 'undefined') return 'unknown'
  const ua = navigator.userAgent
  if (ua.includes('Win')) return 'Windows'
  if (ua.includes('Mac')) return 'macOS'
  if (ua.includes('Linux')) return 'Linux'
  if (ua.includes('Android')) return 'Android'
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS'
  return 'unknown'
}

function getDeviceInfo(): string {
  if (typeof navigator === 'undefined') return 'unknown'
  const ua = navigator.userAgent
  if (ua.includes('Mobile')) return 'mobile'
  if (ua.includes('Tablet') || ua.includes('iPad')) return 'tablet'
  return 'desktop'
}

function getViewport(): string {
  if (typeof window === 'undefined') return 'unknown'
  return `${window.innerWidth}x${window.innerHeight}`
}

function getRouteParams(): Record<string, string> {
  if (typeof window === 'undefined') return {}
  const url = new URL(window.location.href)
  const params: Record<string, string> = {}
  url.searchParams.forEach((v, k) => { params[k] = v })
  // Extract path segments that look like IDs
  const segments = url.pathname.split('/').filter(Boolean)
  segments.forEach((seg, i) => {
    if (seg.match(/^[a-zA-Z0-9_-]{8,}$/) && i > 0) {
      params[`pathParam_${i}`] = seg
    }
  })
  return params
}

function getPageName(): string {
  if (typeof document === 'undefined') return ''
  // Try to extract from page heading
  const h1 = document.querySelector('h2, h1')
  return h1?.textContent?.trim().slice(0, 80) ?? ''
}

// ── Label maps ───────────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<BugCategory, string> = {
  'bug': 'Bug',
  'data-issue': 'Data Issue',
  'ux-issue': 'UX Issue',
  'performance': 'Performance',
  'feature-request': 'Feature Request',
  'other': 'Other',
}

const SEVERITY_LABELS: Record<BugSeverity, string> = {
  'blocker': 'Blocker',
  'high': 'High',
  'medium': 'Medium',
  'low': 'Low',
}

const SEVERITY_COLORS: Record<BugSeverity, string> = {
  'blocker': '#ef4444',
  'high': '#f97316',
  'medium': '#eab308',
  'low': '#22c55e',
}

// ── Component ────────────────────────────────────────────────────────────────

export default function BugReportButton({
  userEmail,
  userName,
  project: mountedProject = DEFAULT_BUG_PROJECT,
  offsetBottom = '1.25rem',
}: Props) {
  const { lang } = useLanguage()
  const t = translations[lang]

  // No session = the reporter must tell us who they are, otherwise the
  // resolution email has nowhere to go and the roster shows an anonymous row.
  const hasSessionIdentity = Boolean(userEmail || userName)

  /**
   * WHICH APP THIS REPORT IS ABOUT (operator directive, Sep 2026: "we need support for entering
   * website bugs — it is ok to file them while logged in [the portal] but we need a specific area
   * for them").
   *
   * The mount point still supplies the DEFAULT (the portal layout mounts `portal`), but a reporter
   * signed into the portal can now say the bug is about the public Swifts site instead. That one
   * choice carries everything downstream: the area list, the form's examples, the tracker the
   * emails brand themselves with, and — because `website` is in PIPELINE_PROJECTS — which repo the
   * fix lane clones. Without it a website bug could only be filed by an operator through the CLI.
   *
   * Only projects the pipeline actually works are offered. `andrei-uber` stays out of the picker:
   * its reports are history nobody works here, and offering it would invite new ones.
   */
  const [project, setProject] = useState<BugProject>(mountedProject)
  // Re-sync if the mount ever changes its project (a surface that swaps apps in place).
  useEffect(() => { setProject(mountedProject) }, [mountedProject])
  const projectChoices = PIPELINE_PROJECTS

  const areaOptions = areasForProject(project)
  // Example copy must follow the project too — the portal's "Planning Lab"
  // examples on the driver form describe screens that do not exist there.
  const examples = examplesForProject(project)
  const projectLabel = bugProjectLabel(project)

  // Client-mount gate (June 2026): render nothing during SSR/hydration and
  // mount the mascot in a separate post-hydration commit. When this floating
  // button hydrated as a sibling of very large client pages (the recipe-book
  // editors, ~7k-line client chunks), React never completed hydration of the
  // page subtree — the recipe grids sat on "Loading recipe books…" forever
  // with no console error (reproduced on /recipes/recipe-books/edibles and
  // /consultant-recipes; bisected to this component). Skipping SSR for the
  // mascot removes it from the hydration pass entirely; it pops in a frame
  // later, which is invisible in practice for a floating toy button.
  const [mascotMounted, setMascotMounted] = useState(false)
  useEffect(() => { setMascotMounted(true) }, [])

  const [open, setOpen] = useState(false)
  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState<{ id: string; title: string } | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  /**
   * The filing this form is HOLDING because the text looks like a credential (slice 2). The typing
   * stays in the fields; "Paste anyway" re-submits the identical report with the override, so the
   * check is a speed bump for somebody with a reason and a wall for the accidental paste.
   */
  const [credHold, setCredHold] = useState<string | null>(null)
  const pasteCredentialAnyway = useRef(false)
  // Ordered list of base64 data URLs, capped at MAX_BUG_SCREENSHOTS.
  // BUG-mskg1hlz-71vl: Jordan asked to attach two shots "for compare/contrast
  // to add more context" — one attachment could not carry a before/after.
  const [screenshots, setScreenshots] = useState<string[]>([])
  // Non-image files (a spreadsheet, a PDF, an export), capped at
  // MAX_BUG_ATTACHMENTS. Jordan, Aug 2026: he tried to attach an Excel file and
  // the picker discarded it silently — `attachError` is why that can't recur.
  const [attachments, setAttachments] = useState<BugAttachment[]>([])
  const [attachError, setAttachError] = useState<string | null>(null)

  // Restore the remembered reporter identity on session-less surfaces. Declared
  // AFTER `form` state so `setForm` is initialized when this closure is built.
  // A session, when present, always wins — those fields aren't even rendered.
  useEffect(() => {
    if (hasSessionIdentity) return
    try {
      const raw = window.localStorage.getItem(REPORTER_STORAGE_KEY)
      if (!raw) return
      const saved = JSON.parse(raw) as { name?: unknown; email?: unknown }
      setForm(prev => ({
        ...prev,
        reporterName: typeof saved.name === 'string' ? saved.name : prev.reporterName,
        reporterEmail: typeof saved.email === 'string' ? saved.email : prev.reporterEmail,
      }))
    } catch { /* corrupt or unavailable storage — just start blank */ }
  }, [hasSessionIdentity])

  /**
   * Clear the form for a fresh report while KEEPING the reporter identity.
   * On a session-less surface, blanking it would make the reporter retype who
   * they are between two bugs filed in the same sitting.
   */
  const resetForm = useCallback(() => {
    setForm(prev => ({ ...EMPTY_FORM, reporterName: prev.reporterName, reporterEmail: prev.reporterEmail }))
  }, [])

  // Capture URL at time of opening
  const capturedUrl = useRef('')
  const capturedRoute = useRef('')
  const capturedPageName = useRef('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  // ── Mascot mood state ──────────────────────────────────────────────────
  const [mood, setMood] = useState<MascotMood>('idle')
  const [reducedMotion, setReducedMotion] = useState(false)
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const moodTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isHovered = useRef(false)
  const isMounted = useRef(true)

  // Track mount status
  useEffect(() => {
    isMounted.current = true
    return () => { isMounted.current = false }
  }, [])

  // Check reduced-motion preference
  useEffect(() => {
    setReducedMotion(prefersReducedMotion())
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)')
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  // Idle mood rotation — robust recursive timer
  useEffect(() => {
    if (reducedMotion || open) return

    let cancelled = false

    function scheduleNextIdle() {
      if (cancelled) return
      const delay = nextIdleDelay()
      idleTimerRef.current = setTimeout(() => {
        if (cancelled || !isMounted.current) return
        if (isHovered.current) { scheduleNextIdle(); return }
        const nextMood = pickIdleMood()
        setMood(nextMood)
        const dur = MOOD_DURATIONS[nextMood]
        moodTimerRef.current = setTimeout(() => {
          if (cancelled || !isMounted.current) return
          setMood('idle')
          scheduleNextIdle()
        }, dur)
      }, delay)
    }

    // Start first idle cycle
    scheduleNextIdle()

    return () => {
      cancelled = true
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      if (moodTimerRef.current) clearTimeout(moodTimerRef.current)
    }
  }, [reducedMotion, open])

  /** Trigger a mood for its defined duration, then return to idle. */
  const triggerMood = useCallback((m: MascotMood) => {
    if (moodTimerRef.current) clearTimeout(moodTimerRef.current)
    setMood(m)
    if (MOOD_DURATIONS[m] !== Infinity) {
      moodTimerRef.current = setTimeout(() => setMood('idle'), MOOD_DURATIONS[m])
    }
  }, [])

  const openModal = useCallback(() => {
    capturedUrl.current = typeof window !== 'undefined' ? window.location.href : ''
    capturedRoute.current = typeof window !== 'undefined' ? window.location.pathname : ''
    capturedPageName.current = getPageName()
    setOpen(true)
    setSuccess(null)
    setSubmitError(null)
    setErrors({})
  }, [])

  // Feedback deep link from the monthly market-report email (`?feedback=newsletter-YYYY-MM`): open the modal
  // PREFILLED for that edition (title, area "Newsletter — <Month>", a prompt in the steps box). Only a submitted form
  // files anything — the report then goes through the ordinary bug-report route. See lib/newsletter/feedback-link.ts.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    const hit = parseNewsletterFeedbackSlug(url.searchParams.get(NEWSLETTER_FEEDBACK_PARAM))
    if (!hit) return
    const pre = newsletterFeedbackPrefill(hit.month)
    setForm(f => ({ ...f, title: pre.title, affectedArea: 'Other', customArea: pre.area, stepsToReproduce: pre.steps, description: pre.description }))
    openModal()
    url.searchParams.delete(NEWSLETTER_FEEDBACK_PARAM)
    window.history.replaceState(null, '', url.toString())
  }, [openModal])

  const closeModal = useCallback(() => {
    setOpen(false)
    if (success) {
      resetForm()
      setScreenshots([])
      setAttachments([])
      setAttachError(null)
      setSuccess(null)
    }
  }, [success, resetForm])

  /**
   * Append images, ignoring any that would exceed the cap and any exact
   * duplicate (a double Ctrl+V of the same clipboard image). Functional
   * setState so a burst of paste/upload events can't drop one another.
   */
  const addScreenshots = useCallback((urls: string[]) => {
    setScreenshots(prev => {
      const next = [...prev]
      for (const u of urls) {
        if (next.length >= MAX_BUG_SCREENSHOTS) break
        if (!u || next.includes(u)) continue
        next.push(u)
      }
      return next
    })
  }, [])

  const removeScreenshot = useCallback((idx: number) => {
    setScreenshots(prev => prev.filter((_, i) => i !== idx))
  }, [])

  /** Same append-with-cap rule as screenshots, over the non-image files. */
  const addAttachments = useCallback((files: BugAttachment[]) => {
    setAttachments(prev => {
      const next = [...prev]
      for (const f of files) {
        if (next.length >= MAX_BUG_ATTACHMENTS) break
        if (!f.dataUrl || next.some(n => n.dataUrl === f.dataUrl)) continue
        next.push(f)
      }
      return next
    })
  }, [])

  const removeAttachment = useCallback((idx: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx))
    setAttachError(null)
  }, [])

  // Paste screenshot(s) from clipboard when modal is open
  useEffect(() => {
    if (!open) return
    function onPaste(e: ClipboardEvent) {
      const items = e.clipboardData?.items
      if (!items) return
      // Take EVERY image on the clipboard, not just the first — pasting a
      // multi-image selection should attach them all (up to the cap).
      const files = Array.from(items)
        .filter(item => item.type.startsWith('image/'))
        .map(item => item.getAsFile())
        .filter((f): f is File => f !== null)
      if (files.length === 0) return
      e.preventDefault()
      Promise.all(files.map(readFileAsDataUrl)).then(addScreenshots)
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [open, addScreenshots])

  function readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  /**
   * Route each picked file to the right bucket and SAY SOMETHING about the ones
   * that don't make it.
   *
   * The bug being fixed here: this used to be one `.filter(f => f.type.startsWith('image/'))`,
   * so picking an .xlsx discarded it with no message — indistinguishable from a
   * broken picker. Every rejection below now names the file and the reason.
   */
  function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? [])
    // Reset the input FIRST so picking the SAME file again still fires onChange —
    // otherwise re-adding a file you just removed silently does nothing.
    e.target.value = ''
    if (picked.length === 0) return

    const rejected: string[] = []
    const images: File[] = []
    const files: File[] = []
    for (const f of picked) {
      if (f.size > MAX_ATTACHMENT_BYTES) {
        rejected.push(`"${f.name}" is ${mbLabel(f.size)} — the limit is ${mbLabel(MAX_ATTACHMENT_BYTES)} per file`)
      } else if (f.type.startsWith('image/')) {
        images.push(f)
      } else if (isAllowedAttachmentName(f.name)) {
        files.push(f)
      } else {
        rejected.push(`"${f.name}" is not a supported type — attach ${BUG_ATTACHMENT_EXTENSIONS.join(', ')} or an image`)
      }
    }
    if (images.length > 0 && screenshots.length >= MAX_BUG_SCREENSHOTS) {
      rejected.push(`Already at ${MAX_BUG_SCREENSHOTS} screenshots — remove one to add another`)
    }
    if (files.length > 0 && attachments.length >= MAX_BUG_ATTACHMENTS) {
      rejected.push(`Already at ${MAX_BUG_ATTACHMENTS} files — remove one to add another`)
    }
    setAttachError(rejected.length > 0 ? rejected.join(' · ') : null)

    if (images.length > 0) Promise.all(images.map(readFileAsDataUrl)).then(addScreenshots)
    if (files.length > 0) {
      Promise.all(files.map(async f => ({ name: f.name, type: f.type, dataUrl: await readFileAsDataUrl(f) })))
        .then(addAttachments)
    }
  }

  // Keyboard shortcut: Ctrl+Shift+B
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.ctrlKey && e.shiftKey && e.key === 'B') {
        e.preventDefault()
        openModal()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [openModal])

  function patch(updates: Partial<FormState>) {
    setForm(prev => ({ ...prev, ...updates }))
    // Clear errors on the fields being updated
    const clearedErrors = { ...errors }
    for (const key of Object.keys(updates)) {
      delete clearedErrors[key]
    }
    setErrors(clearedErrors)
  }

  async function handleSubmit() {
    // Client-side validation
    const newErrors: Record<string, string> = {}
    if (!form.title.trim() || form.title.trim().length < 5) newErrors.title = 'Title must be at least 5 characters'
    if (!form.description.trim() || form.description.trim().length < 20) newErrors.description = 'Describe what happened (at least 20 characters)'
    if (!form.category) newErrors.category = 'Select a category'
    if (!form.severity) newErrors.severity = 'Select a severity'
    if (!form.affectedArea && !form.customArea.trim()) newErrors.affectedArea = 'Select or enter an affected area'
    if (!form.stepsToReproduce.trim() || form.stepsToReproduce.trim().length < 10) newErrors.stepsToReproduce = 'Describe how to reproduce (at least 10 characters)'
    // Identity is required only where there is no session to take it from.
    // Without an email the resolution notice has no recipient, so the reporter
    // would never hear that their bug was fixed.
    if (!hasSessionIdentity) {
      if (!form.reporterName.trim()) newErrors.reporterName = 'Tell us who is reporting this'
      const email = form.reporterEmail.trim()
      if (!email) newErrors.reporterEmail = 'We need an email to tell you when this is fixed'
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.reporterEmail = 'That does not look like an email address'
    }

    // Everything rides in ONE request body, and the platform rejects an oversized
    // one before any of our code runs — the reporter sees a bare "network error"
    // with no idea which of their four phone screenshots to drop
    // (BUG-mtkx9th0-g27g). Measure it HERE, with the same helper the server
    // validates with, and refuse in language that names the fix.
    const uploadBytes = bugUploadBytes({ screenshotUrls: screenshots, attachments })
    if (uploadBytes > MAX_BUG_UPLOAD_BYTES) {
      newErrors.attachments = bugUploadTooLargeMessage(uploadBytes)
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    // THE PASTED-CREDENTIAL CHECK, before the round trip. Only the free-prose fields — the same
    // ones the API screens — never the attachment bytes, where a shape detector is most likely to
    // be wrong and most expensive to be wrong, because the person loses their report.
    if (!pasteCredentialAnyway.current) {
      const screen = screenPastedCredentialWrite({
        title: form.title,
        description: form.description,
        expectedBehavior: form.expectedBehavior,
        stepsToReproduce: form.stepsToReproduce,
        workaroundDetails: form.workaroundDetails,
      })
      if (!screen.ok) {
        setCredHold(screen.message)
        return
      }
    }

    setSubmitting(true)
    setSubmitError(null)
    setCredHold(null)

    const area = form.affectedArea === 'Other' || !form.affectedArea
      ? form.customArea.trim() || 'Other'
      : form.affectedArea

    const reproductionContext = JSON.stringify({
      pageName: capturedPageName.current,
      routeParams: getRouteParams(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    })

    // A session identity always wins; the typed fields are the fallback for
    // surfaces that have no login at all.
    const reporterName = (userName || form.reporterName).trim()
    const reporterEmail = (userEmail || form.reporterEmail).trim()

    if (!hasSessionIdentity) {
      try {
        window.localStorage.setItem(
          REPORTER_STORAGE_KEY,
          JSON.stringify({ name: reporterName, email: reporterEmail }),
        )
      } catch { /* storage unavailable — the reporter just retypes next time */ }
    }

    try {
      const res = await fetch('/api/bug-reports', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Share-link surfaces have no session cookie; this header is what
          // authorizes the create. Omitted entirely inside the portal.
        },
        body: JSON.stringify({
          // The mount point owns the tag. The server re-normalizes it, and
          // FORCES it when the request authenticated with a share-link key.
          project,
          title: form.title.trim(),
          description: form.description.trim(),
          expectedBehavior: form.expectedBehavior.trim(),
          stepsToReproduce: form.stepsToReproduce.trim(),
          category: form.category,
          severity: form.severity,
          affectedArea: area,
          environment: form.environment,
          frequency: form.frequency || 'once',
          blockedWork: form.blockedWork,
          workaroundFound: form.workaroundFound,
          workaroundDetails: form.workaroundDetails.trim(),
          currentUrl: capturedUrl.current,
          currentRoute: capturedRoute.current,
          reporterName,
          reporterEmail,
          browserInfo: getBrowserInfo(),
          osInfo: getOsInfo(),
          deviceInfo: getDeviceInfo(),
          viewport: getViewport(),
          appVersion: '',
          reproductionContext,
          // `screenshotUrls` is the wire format the server normalizes into the
          // stored primary/extras pair, and it is the ONLY place an image rides.
          //
          // The legacy `screenshotUrl` used to be sent alongside it "for safety" — a
          // pure duplicate, because `normalizeScreenshotInput` discards it whenever the
          // array is present and fills the stored legacy field from the array itself.
          // It cost a second copy of the FIRST image on the wire, base64-inflated: one
          // 2.4 MB photo left as ~6.4 MB against the platform's 4.5 MB body cap, so the
          // request died at the edge and the 413 branch below printed our own (accurate)
          // "total 2.4 MB, limit 3.2 MB" — a size refusal for a report that was inside
          // the budget (BUG-mur2a4mg-tsb9). Four small screenshots fitted; one big one
          // did not, which is why it reads as happening "once".
          screenshotUrls: screenshots,
          attachments,
          // Transport-only, and stripped server-side before the row is written.
          ...(pasteCredentialAnyway.current ? { [PASTED_CREDENTIAL_OVERRIDE_KEY]: true } : {}),
        }),
      })

      if (!res.ok) {
        // A refusal is not always OURS. The platform's own 413 / 502 / gateway
        // pages are HTML, so `res.json()` THREW and fell into the catch below —
        // which is how a body-size rejection reached the reporter as "network
        // error" (BUG-mtkx9th0-g27g). Read the body defensively and say what the
        // status actually means.
        const data = await res.json().catch(() => null) as
          { errors?: Array<{ field: string; message: string }>; error?: string; code?: string } | null
        // The pasted-credential refusal is the server's, not a field error: it gets the explanation
        // and the override, not a red line under one input. This branch is the backstop for the
        // client check above — the server is the wall.
        if (res.status === 422 && data?.code === 'PASTED_CREDENTIAL') {
          setCredHold(String(data.error ?? ''))
          triggerMood(errorMood())
          return
        }
        if (data?.errors) {
          const serverErrors: Record<string, string> = {}
          for (const e of data.errors) serverErrors[e.field] = e.message
          setErrors(serverErrors)
        } else if (res.status === 413) {
          setErrors({ attachments: bugUploadTooLargeMessage(uploadBytes) })
        } else {
          setSubmitError(data?.error || `Failed to submit bug report (${res.status})`)
        }
        triggerMood(errorMood())
        return
      }

      const created = await res.json()
      setSuccess({ id: created.id, title: created.title })
      triggerMood(successMood())
    } catch (err) {
      setSubmitError('Network error — please try again')
      triggerMood(errorMood())
    } finally {
      setSubmitting(false)
    }
  }

  // ── Styles ──────────────────────────────────────────────────────────────

  const label: React.CSSProperties = {
    display: 'block', fontSize: '0.7rem', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.04em',
    color: 'var(--t-text-5)', marginBottom: '0.3rem',
  }
  const inputBase: React.CSSProperties = {
    width: '100%', padding: '0.5rem 0.65rem', borderRadius: 8,
    border: '1px solid var(--t-border-2)', background: 'var(--t-input-bg, var(--t-surface))',
    color: 'var(--t-text)', fontSize: '0.85rem', fontFamily: 'inherit',
    boxSizing: 'border-box',
  }
  const errorStyle: React.CSSProperties = {
    color: '#ef4444', fontSize: '0.7rem', marginTop: '0.2rem',
  }

  // ── Render ──────────────────────────────────────────────────────────────

  // Post-hydration mount gate — see comment at the top of the component.
  // (Placed after all hooks so the hook order is identical on every render.)
  if (!mascotMounted) return null

  // Button-level animation class per mood
  const btnAnimClass = reducedMotion ? '' : `bm-btn-${mood}`

  // Dynamic glow per mood
  const glowShadow =
    mood === 'celebrate' ? '0 0 32px rgba(34,197,94,0.6), 0 0 12px rgba(250,204,21,0.3), 0 6px 24px rgba(0,0,0,0.3)'
    : mood === 'worried' ? '0 0 22px rgba(239,68,68,0.45), 0 4px 20px rgba(0,0,0,0.35)'
    : mood === 'excited' ? '0 0 22px rgba(34,197,94,0.45), 0 4px 20px rgba(0,0,0,0.35)'
    : mood === 'cry' ? '0 0 16px rgba(96,165,250,0.35), 0 4px 20px rgba(0,0,0,0.35)'
    : mood === 'sneeze' ? '0 0 16px rgba(251,191,36,0.35), 0 4px 20px rgba(0,0,0,0.35)'
    : mood === 'laugh' ? '0 0 16px rgba(251,146,60,0.3), 0 4px 20px rgba(0,0,0,0.35)'
    : mood === 'growPop' ? '0 0 24px rgba(168,85,247,0.4), 0 4px 20px rgba(0,0,0,0.35)'
    : mood === 'sleepy' ? '0 0 12px rgba(167,139,250,0.3), 0 4px 20px rgba(0,0,0,0.35)'
    : mood === 'running' ? '0 0 16px rgba(34,197,94,0.35), 0 4px 20px rgba(0,0,0,0.35)'
    : mood === 'kicking' ? '0 0 18px rgba(249,115,22,0.4), 0 4px 20px rgba(0,0,0,0.35)'
    : '0 4px 20px rgba(0,0,0,0.35)'

  return (
    <>
      {/* Button-level animation styles */}
      <style>{`
        .bm-btn-idle {
          animation: bm-btn-float 3s ease-in-out infinite;
        }
        @keyframes bm-btn-float {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-3px); }
        }
        .bm-btn-blink { animation: bm-btn-float 3s ease-in-out infinite; }
        .bm-btn-excited {
          animation: bm-btn-hop 0.5s ease-out !important;
        }
        @keyframes bm-btn-hop {
          0%   { transform: translateY(0) scale(1); }
          30%  { transform: translateY(-12px) scale(1.08); }
          55%  { transform: translateY(3px) scale(0.95); }
          75%  { transform: translateY(-3px) scale(1.02); }
          100% { transform: translateY(0) scale(1); }
        }
        .bm-btn-celebrate {
          animation: bm-btn-celebrate 1.2s ease-out !important;
        }
        @keyframes bm-btn-celebrate {
          0%   { transform: translateY(0) scale(1) rotate(0); }
          10%  { transform: translateY(-18px) scale(1.12); }
          25%  { transform: translateY(-14px) rotate(6deg); }
          40%  { transform: translateY(-8px) rotate(-6deg); }
          55%  { transform: translateY(4px) scale(0.94); }
          70%  { transform: translateY(-3px) scale(1.02); }
          100% { transform: translateY(0) scale(1) rotate(0); }
        }
        .bm-btn-worried {
          animation: bm-btn-shake 0.6s ease-in-out !important;
        }
        @keyframes bm-btn-shake {
          0%, 100% { transform: translateX(0) rotate(0); }
          12%      { transform: translateX(-6px) rotate(-3deg); }
          25%      { transform: translateX(6px) rotate(3deg); }
          37%      { transform: translateX(-5px) rotate(-2deg); }
          50%      { transform: translateX(5px) rotate(2deg); }
          62%      { transform: translateX(-3px) rotate(-1deg); }
          75%      { transform: translateX(2px); }
        }
        .bm-btn-laugh {
          animation: bm-btn-laugh 1.8s ease-in-out !important;
        }
        @keyframes bm-btn-laugh {
          0%   { transform: translateY(0) rotate(0); }
          10%  { transform: translateY(-8px) rotate(3deg); }
          22%  { transform: translateY(2px) rotate(-2deg); }
          34%  { transform: translateY(-6px) rotate(2deg); }
          46%  { transform: translateY(1px) rotate(-1deg); }
          58%  { transform: translateY(-3px); }
          70%  { transform: translateY(0); }
          100% { transform: translateY(0) rotate(0); }
        }
        .bm-btn-cry {
          animation: bm-btn-tremble 0.35s ease-in-out infinite !important;
        }
        @keyframes bm-btn-tremble {
          0%, 100% { transform: translateX(0) translateY(0); }
          25%      { transform: translateX(-2px) translateY(1px); }
          75%      { transform: translateX(2px) translateY(1px); }
        }
        .bm-btn-sneeze {
          animation: bm-btn-sneeze 1.4s ease-in-out !important;
        }
        @keyframes bm-btn-sneeze {
          0%   { transform: translateY(0) scale(1); }
          20%  { transform: translateY(-4px) scale(1.05); }
          35%  { transform: translateY(-6px) scale(1.08); }
          42%  { transform: translateY(8px) scale(0.9) rotate(5deg); }
          55%  { transform: translateY(4px) scale(0.95) rotate(2deg); }
          70%  { transform: translateY(0) scale(1) rotate(0); }
          100% { transform: translateY(0) scale(1); }
        }
        .bm-btn-sleepy {
          animation: bm-btn-sway 2.5s ease-in-out infinite !important;
        }
        @keyframes bm-btn-sway {
          0%, 100% { transform: translateY(0) rotate(0); }
          50%      { transform: translateY(3px) rotate(-5deg); }
        }
        .bm-btn-curious {
          animation: bm-btn-tilt 1.8s ease-in-out !important;
        }
        @keyframes bm-btn-tilt {
          0%   { transform: rotate(0) translateY(0); }
          25%  { transform: rotate(10deg) translateY(-4px); }
          65%  { transform: rotate(-5deg); }
          100% { transform: rotate(0); }
        }
        .bm-btn-hungry {
          animation: bm-btn-rumble 2s ease-in-out !important;
        }
        @keyframes bm-btn-rumble {
          0%   { transform: scale(1); }
          10%  { transform: scale(1.06) rotate(1deg); }
          20%  { transform: scale(0.95) rotate(-1deg); }
          30%  { transform: scale(1.04) rotate(1deg); }
          40%  { transform: scale(0.97); }
          55%  { transform: scale(1.02); }
          70%  { transform: scale(1); }
        }
        .bm-btn-thinking {
          animation: bm-btn-think 2.2s ease-in-out !important;
        }
        @keyframes bm-btn-think {
          0%   { transform: rotate(0); }
          15%  { transform: rotate(-5deg) translateY(-2px); }
          80%  { transform: rotate(-5deg) translateY(-2px); }
          100% { transform: rotate(0); }
        }
        .bm-btn-growPop {
          animation: bm-btn-growpop 2.2s ease-in-out !important;
        }
        @keyframes bm-btn-growpop {
          0%   { transform: scale(1); }
          25%  { transform: scale(1.3); }
          40%  { transform: scale(1.4); }
          48%  { transform: scale(0.2); opacity: 0.3; }
          55%  { transform: scale(0.2); opacity: 0; }
          68%  { transform: scale(0.6); opacity: 0.6; }
          80%  { transform: scale(1.08); opacity: 1; }
          90%  { transform: scale(0.96); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        .bm-btn-running {
          animation: bm-btn-run 0.4s ease-in-out infinite !important;
        }
        @keyframes bm-btn-run {
          0%, 100% { transform: translateY(0) translateX(0); }
          25%      { transform: translateY(-5px) translateX(2px); }
          50%      { transform: translateY(0) translateX(0); }
          75%      { transform: translateY(-5px) translateX(-2px); }
        }
        .bm-btn-kicking {
          animation: bm-btn-kick 2.2s ease-in-out !important;
        }
        @keyframes bm-btn-kick {
          0%   { transform: translateX(0) rotate(0); }
          20%  { transform: translateX(-4px) rotate(-3deg); }
          35%  { transform: translateX(-6px) rotate(-6deg); }
          45%  { transform: translateX(6px) rotate(6deg); }
          55%  { transform: translateX(3px) rotate(3deg); }
          70%  { transform: translateX(0) rotate(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          [class^="bm-btn-"] {
            animation: none !important;
          }
        }
      `}</style>

      {/* Floating mascot button */}
      <button
        className={btnAnimClass}
        onClick={openModal}
        // Parenthesised, not "a ${label} bug" — the article would be wrong for
        // any project whose name starts with a vowel ("a AndreiUber bug").
        title={`Report a bug — ${projectLabel} (Ctrl+Shift+B)`}
        aria-label={`Report a bug — ${projectLabel}`}
        style={{
          position: 'fixed', bottom: offsetBottom, right: '1.25rem', zIndex: 9999,
          width: 72, height: 72, borderRadius: '50%',
          background: 'radial-gradient(circle at 40% 35%, rgba(34,197,94,0.18), rgba(0,0,0,0.15) 70%)',
          border: '2px solid rgba(34,197,94,0.25)',
          cursor: 'pointer',
          boxShadow: glowShadow,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: reducedMotion ? 'none' : 'box-shadow 0.35s ease, border-color 0.35s ease',
          outline: 'none',
          padding: 0,
        }}
        onMouseEnter={() => {
          isHovered.current = true
          if (!reducedMotion) triggerMood(hoverMood())
        }}
        onMouseLeave={() => {
          isHovered.current = false
          if (mood === 'excited') setMood('idle')
        }}
        onFocus={e => {
          e.currentTarget.style.borderColor = 'rgba(34,197,94,0.6)'
          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34,197,94,0.3), 0 4px 20px rgba(0,0,0,0.35)'
        }}
        onBlur={e => {
          e.currentTarget.style.borderColor = 'rgba(34,197,94,0.2)'
          e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.35)'
        }}
      >
        <BugMascot mood={mood} reducedMotion={reducedMotion} size={58} />
      </button>

      {/* Modal overlay */}
      {open && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(0,0,0,0.6)', display: 'flex',
            alignItems: 'flex-start', justifyContent: 'center',
            padding: '2rem 1rem', overflowY: 'auto',
          }}
          onClick={e => { if (e.target === e.currentTarget) closeModal() }}
        >
          <div style={{
            background: 'var(--t-surface)', borderRadius: 16,
            border: '1px solid var(--t-border)', maxWidth: 680,
            width: '100%', padding: '1.75rem', color: 'var(--t-text)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
            maxHeight: '90vh', overflowY: 'auto',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                &#128027; {t.bugReport?.modalTitle ?? 'Report a Bug'}
                {/* Name the project on the form itself: the same control is
                    mounted on two different apps, and a reporter who cannot
                    see which queue they are filing into cannot catch a
                    mis-mounted button. */}
                <span style={{
                  fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase',
                  letterSpacing: '0.05em', padding: '0.15rem 0.5rem', borderRadius: 5,
                  background: 'var(--t-border)', color: 'var(--t-text-4)',
                }}>{projectLabel}</span>
              </h2>
              <button onClick={closeModal} style={{
                background: 'transparent', border: 'none', color: 'var(--t-text-4)',
                cursor: 'pointer', fontSize: '1.5rem', lineHeight: 1, padding: '0.25rem',
              }}>&times;</button>
            </div>

            {/* Success state */}
            {success && (
              <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>&#9989;</div>
                <h3 style={{ marginBottom: '0.5rem', fontWeight: 700, fontSize: '1.1rem' }}>Bug Report Submitted</h3>
                <p style={{ color: 'var(--t-text-4)', fontSize: '0.85rem', marginBottom: '1rem' }}>
                  <strong>{success.title}</strong> — ID: <code style={{ background: 'var(--t-border)', padding: '0.15rem 0.4rem', borderRadius: 4, fontSize: '0.8rem' }}>{success.id}</code>
                </p>
                {/* The bug page lives behind the portal login. Offering it on
                    a share-link surface would send the reporter to a sign-in
                    wall for an account they don't have — so it renders only
                    where the viewer is actually signed in. */}
                {hasSessionIdentity ? (
                  <>
                    <a
                      href={`/settings/bugs/${success.id}`}
                      style={{
                        display: 'inline-block', background: '#2563eb', color: '#fff',
                        padding: '0.5rem 1.5rem', borderRadius: 8, textDecoration: 'none',
                        fontWeight: 700, fontSize: '0.85rem', marginBottom: '0.75rem',
                      }}
                    >
                      View Bug Report
                    </a>
                    <br />
                  </>
                ) : (
                  <p style={{ color: 'var(--t-text-5)', fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                    {/* Explicit {' '} — the literal space after </strong> was
                        being collapsed away, rendering "…@x.comwhen it's fixed". */}
                    We&apos;ll email you at <strong>{form.reporterEmail.trim()}</strong>{' '}when it&apos;s fixed.
                  </p>
                )}
                <button onClick={() => { resetForm(); setScreenshots([]); setAttachments([]); setAttachError(null); setSuccess(null) }} style={{
                  background: 'transparent', border: '1px solid var(--t-border-2)',
                  color: 'var(--t-text-4)', padding: '0.4rem 1rem', borderRadius: 8,
                  cursor: 'pointer', fontSize: '0.8rem', marginTop: '0.5rem',
                }}>File Another</button>
              </div>
            )}

            {/* Form */}
            {!success && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
                {/* Auto-captured context bar */}
                <div style={{ background: 'var(--t-border)', borderRadius: 8, padding: '0.5rem 0.75rem', fontSize: '0.7rem', color: 'var(--t-text-5)' }}>
                  Auto-captured: {capturedRoute.current || '/'} &middot; {getBrowserInfo()} &middot; {getOsInfo()} &middot; {getViewport()}
                </div>

                {/* Reporter identity — only where there is no session to read
                    it from. The email is what the "your bug is fixed" notice
                    is sent to. */}
                {!hasSessionIdentity && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <div>
                      <label style={label}>Your Name *</label>
                      <input
                        style={{ ...inputBase, ...(errors.reporterName ? { borderColor: '#ef4444' } : {}) }}
                        value={form.reporterName}
                        onChange={e => patch({ reporterName: e.target.value })}
                        placeholder="Who is reporting this?"
                        autoComplete="name"
                        maxLength={80}
                      />
                      {errors.reporterName && <div style={errorStyle}>{errors.reporterName}</div>}
                    </div>
                    <div>
                      <label style={label}>Your Email *</label>
                      <input
                        type="email"
                        style={{ ...inputBase, ...(errors.reporterEmail ? { borderColor: '#ef4444' } : {}) }}
                        value={form.reporterEmail}
                        onChange={e => patch({ reporterEmail: e.target.value })}
                        placeholder="So we can tell you when it's fixed"
                        autoComplete="email"
                        maxLength={120}
                      />
                      {errors.reporterEmail && <div style={errorStyle}>{errors.reporterEmail}</div>}
                    </div>
                  </div>
                )}

                {/* Title */}
                <div>
                  <label style={label}>Title *</label>
                  <input
                    style={{ ...inputBase, ...(errors.title ? { borderColor: '#ef4444' } : {}) }}
                    value={form.title}
                    onChange={e => patch({ title: e.target.value })}
                    placeholder={examples.title}
                    maxLength={200}
                  />
                  {errors.title && <div style={errorStyle}>{errors.title}</div>}
                </div>

                {/* Category + Severity row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <label style={label}>Category *</label>
                    <select
                      style={{ ...inputBase, ...(errors.category ? { borderColor: '#ef4444' } : {}) }}
                      value={form.category}
                      onChange={e => patch({ category: e.target.value as BugCategory })}
                    >
                      <option value="">Select...</option>
                      {BUG_CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
                    </select>
                    {errors.category && <div style={errorStyle}>{errors.category}</div>}
                  </div>
                  <div>
                    <label style={label}>Severity *</label>
                    <select
                      style={{ ...inputBase, ...(errors.severity ? { borderColor: '#ef4444' } : {}) }}
                      value={form.severity}
                      onChange={e => patch({ severity: e.target.value as BugSeverity })}
                    >
                      <option value="">Select...</option>
                      {BUG_SEVERITIES.map(s => (
                        <option key={s} value={s} style={{ color: SEVERITY_COLORS[s] }}>{SEVERITY_LABELS[s]}</option>
                      ))}
                    </select>
                    {errors.severity && <div style={errorStyle}>{errors.severity}</div>}
                  </div>
                </div>

                {/* WHICH APP — above the area, because it CHANGES the area list. A reporter who
                    picks their area first and then switches app would silently lose it, so this
                    control sits before the thing it governs and clears a now-invalid area. */}
                {projectChoices.length > 1 && (
                  <div>
                    <label style={label}>What is this bug about? *</label>
                    <select
                      style={inputBase}
                      value={project}
                      onChange={e => {
                        const next = e.target.value as BugProject
                        setProject(next)
                        // The two apps share no areas, so an area chosen for the other one is not
                        // merely odd — it would land the report in a bucket its own picker cannot
                        // show. Drop it (and its free-text twin) rather than carry it across.
                        if (!areasForProject(next).includes(form.affectedArea)) {
                          patch({ affectedArea: '', customArea: '' })
                        }
                      }}
                    >
                      {projectChoices.map(p => (
                        <option key={p} value={p}>{bugProjectLabel(p)}</option>
                      ))}
                    </select>
                    <div style={{ fontSize: '0.7rem', color: 'var(--t-text-5)', marginTop: '0.3rem' }}>
                      {hintForProject(project)}
                    </div>
                  </div>
                )}

                {/* Affected Area + Environment */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <label style={label}>Affected Area *</label>
                    <select
                      style={{ ...inputBase, ...(errors.affectedArea ? { borderColor: '#ef4444' } : {}) }}
                      value={form.affectedArea}
                      onChange={e => patch({ affectedArea: e.target.value })}
                    >
                      <option value="">Select...</option>
                      {areaOptions.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                    {form.affectedArea === 'Other' && (
                      <input
                        style={{ ...inputBase, marginTop: '0.35rem' }}
                        value={form.customArea}
                        onChange={e => patch({ customArea: e.target.value })}
                        placeholder="Describe the area..."
                      />
                    )}
                    {errors.affectedArea && <div style={errorStyle}>{errors.affectedArea}</div>}
                  </div>
                  <div>
                    <label style={label}>Environment</label>
                    <select
                      style={inputBase}
                      value={form.environment}
                      onChange={e => patch({ environment: e.target.value as BugEnvironment })}
                    >
                      {BUG_ENVIRONMENTS.map(env => <option key={env} value={env}>{env}</option>)}
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label style={label}>What happened? *</label>
                  <textarea
                    style={{ ...inputBase, minHeight: 80, resize: 'vertical', ...(errors.description ? { borderColor: '#ef4444' } : {}) }}
                    value={form.description}
                    onChange={e => patch({ description: e.target.value })}
                    placeholder='Describe the issue in detail. What did you see? What data was wrong?'
                  />
                  {errors.description && <div style={errorStyle}>{errors.description}</div>}
                </div>

                {/* Expected behavior */}
                <div>
                  <label style={label}>Expected Behavior</label>
                  <textarea
                    style={{ ...inputBase, minHeight: 50, resize: 'vertical' }}
                    value={form.expectedBehavior}
                    onChange={e => patch({ expectedBehavior: e.target.value })}
                    placeholder='What should have happened instead?'
                  />
                </div>

                {/* Steps to reproduce */}
                <div>
                  <label style={label}>Steps to Reproduce *</label>
                  <textarea
                    style={{ ...inputBase, minHeight: 70, resize: 'vertical', ...(errors.stepsToReproduce ? { borderColor: '#ef4444' } : {}) }}
                    value={form.stepsToReproduce}
                    onChange={e => patch({ stepsToReproduce: e.target.value })}
                    placeholder={examples.steps}
                  />
                  {errors.stepsToReproduce && <div style={errorStyle}>{errors.stepsToReproduce}</div>}
                </div>

                {/* Frequency + Blocked + Workaround row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <label style={label}>Frequency</label>
                    <select style={inputBase} value={form.frequency} onChange={e => patch({ frequency: e.target.value as BugFrequency })}>
                      <option value="">Select...</option>
                      {BUG_FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={label}>Blocked Work?</label>
                    <select style={inputBase} value={form.blockedWork ? 'yes' : 'no'} onChange={e => patch({ blockedWork: e.target.value === 'yes' })}>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                  <div>
                    <label style={label}>Workaround?</label>
                    <select style={inputBase} value={form.workaroundFound ? 'yes' : 'no'} onChange={e => patch({ workaroundFound: e.target.value === 'yes' })}>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                </div>

                {/* Workaround details (conditional) */}
                {form.workaroundFound && (
                  <div>
                    <label style={label}>Workaround Details</label>
                    <textarea
                      style={{ ...inputBase, minHeight: 45, resize: 'vertical' }}
                      value={form.workaroundDetails}
                      onChange={e => patch({ workaroundDetails: e.target.value })}
                      placeholder="Describe the workaround you found..."
                    />
                  </div>
                )}

                {/* Attachments — up to MAX_BUG_SCREENSHOTS images (so a reporter
                    can attach a before/after pair, BUG-mskg1hlz-71vl) PLUS up to
                    MAX_BUG_ATTACHMENTS non-image files. The section used to be
                    images-only and said so; Jordan tried to attach a spreadsheet
                    and the picker discarded it without a word. */}
                <div>
                  <label style={label}>
                    Attachments
                    {(screenshots.length > 0 || attachments.length > 0) && (
                      <span style={{ color: 'var(--t-text-5)', fontWeight: 400, marginLeft: '0.35rem' }}>
                        {screenshots.length > 0 && `${screenshots.length} of ${MAX_BUG_SCREENSHOTS} images`}
                        {screenshots.length > 0 && attachments.length > 0 && ' · '}
                        {attachments.length > 0 && `${attachments.length} of ${MAX_BUG_ATTACHMENTS} files`}
                      </span>
                    )}
                  </label>

                  {screenshots.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      {screenshots.map((src, i) => (
                        <div key={i} style={{ position: 'relative', display: 'inline-block' }}>
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img
                            src={src}
                            alt={`Screenshot ${i + 1}`}
                            style={{ maxWidth: '100%', maxHeight: 140, borderRadius: 8, border: '1px solid var(--t-border)', display: 'block' }}
                          />
                          <span style={{
                            position: 'absolute', bottom: 4, left: 4,
                            background: 'rgba(0,0,0,0.65)', color: '#fff',
                            borderRadius: 4, padding: '0 5px',
                            fontSize: '0.65rem', fontWeight: 700,
                          }}>{i + 1}</span>
                          <button
                            type="button"
                            aria-label={`Remove screenshot ${i + 1}`}
                            onClick={() => removeScreenshot(i)}
                            style={{
                              position: 'absolute', top: 4, right: 4,
                              background: 'rgba(0,0,0,0.7)', color: '#fff', border: 'none',
                              borderRadius: '50%', width: 22, height: 22,
                              cursor: 'pointer', fontSize: '0.75rem', lineHeight: 1,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >&times;</button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Non-image files. Listed as rows with their filename, not
                      thumbnails — a spreadsheet has nothing to preview, and the
                      name IS the information ("Q3 sales.xlsx"). */}
                  {attachments.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.5rem' }}>
                      {attachments.map((f, i) => (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'center', gap: '0.5rem',
                          border: '1px solid var(--t-border)', borderRadius: 6,
                          padding: '0.35rem 0.5rem', fontSize: '0.78rem',
                          background: 'var(--t-input-bg, var(--t-surface))',
                        }}>
                          <span aria-hidden>📎</span>
                          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                          <span style={{ color: 'var(--t-text-5)', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>{mbLabel(dataUrlBytes(f.dataUrl))}</span>
                          <button
                            type="button"
                            aria-label={`Remove ${f.name}`}
                            onClick={() => removeAttachment(i)}
                            style={{ background: 'transparent', border: 'none', color: 'var(--t-text-5)', cursor: 'pointer', fontSize: '1rem', lineHeight: 1, padding: '0 2px' }}
                          >&times;</button>
                        </div>
                      ))}
                    </div>
                  )}

                  {screenshots.length < MAX_BUG_SCREENSHOTS || attachments.length < MAX_BUG_ATTACHMENTS ? (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      style={{
                        border: '2px dashed var(--t-border-2)', borderRadius: 8,
                        padding: '0.75rem', textAlign: 'center', cursor: 'pointer',
                        color: 'var(--t-text-5)', fontSize: '0.78rem',
                        background: 'var(--t-input-bg, var(--t-surface))',
                      }}
                    >
                      {screenshots.length === 0 && attachments.length === 0
                        ? `Paste a screenshot (Ctrl+V) or click to upload — images, or a spreadsheet / PDF`
                        : 'Paste or click to add another'}
                    </div>
                  ) : (
                    <div style={{ fontSize: '0.75rem', color: 'var(--t-text-5)' }}>
                      Attachment limit reached — remove one to add another.
                    </div>
                  )}

                  {/* The whole point of the fix: a file that does NOT make it in
                      says so. Silently dropping one reads as a broken picker. */}
                  {attachError && (
                    <div style={{ marginTop: '0.4rem', fontSize: '0.75rem', color: '#dc2626' }}>{attachError}</div>
                  )}

                  {/* …and so does a refusal that came from the SUBMIT rather than the
                      picker. `errors.attachments` / `errors.screenshotUrls` were set
                      by both the pre-flight and the server and rendered by nothing, so
                      an over-size report simply stopped submitting with no explanation
                      anywhere on the form (BUG-mtkx9th0-g27g). */}
                  {(errors.attachments || errors.screenshotUrls) && (
                    <div data-testid="bug-attachment-error" style={{ marginTop: '0.4rem', fontSize: '0.75rem', color: '#dc2626' }}>
                      {errors.attachments || errors.screenshotUrls}
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={BUG_UPLOAD_ACCEPT}
                    multiple
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                </div>

                {/* The held filing — the explanation, and the door (slice 2) */}
                {credHold && (
                  <div data-testid="pasted-credential-hold"
                    style={{ background: '#fffbeb', border: '1px solid #b45309', borderRadius: 8, padding: '0.6rem 0.75rem', color: '#92400e', fontSize: '0.8rem', lineHeight: 1.45 }}>
                    <div style={{ fontWeight: 800 }}>🔐 Not filed — that looks like a credential</div>
                    <div style={{ marginTop: '0.25rem', whiteSpace: 'pre-wrap' }}>{credHold}</div>
                    <div style={{ marginTop: '0.45rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <button data-testid="pasted-credential-paste-anyway"
                        onClick={() => { pasteCredentialAnyway.current = true; setCredHold(null); void handleSubmit() }}
                        style={{ cursor: 'pointer', background: 'transparent', color: '#92400e', border: '1px solid #b45309', borderRadius: 8, padding: '0.3rem 0.6rem', fontSize: '0.76rem', fontWeight: 700 }}>
                        Paste anyway
                      </button>
                      <button onClick={() => setCredHold(null)}
                        style={{ cursor: 'pointer', background: 'transparent', color: '#92400e', border: '1px solid #d6d3d1', borderRadius: 8, padding: '0.3rem 0.6rem', fontSize: '0.76rem', fontWeight: 700 }}>
                        Let me edit it
                      </button>
                    </div>
                  </div>
                )}

                {/* Submit error */}
                {submitError && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '0.6rem 0.75rem', color: '#dc2626', fontSize: '0.8rem' }}>
                    {submitError}
                  </div>
                )}

                {/* Submit button */}
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  style={{
                    width: '100%', padding: '0.7rem',
                    background: submitting ? 'var(--t-border)' : '#ef4444',
                    color: '#fff', border: 'none', borderRadius: 10,
                    fontWeight: 700, fontSize: '0.9rem', cursor: submitting ? 'not-allowed' : 'pointer',
                    transition: 'background 0.15s',
                  }}
                >
                  {submitting ? 'Submitting...' : 'Submit Bug Report'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
